import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowRightFromBracket';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { TypedMemo } from '../../../common/types';
import { useUserToken } from '../../../../redux/slices/appInfo/hooks';
import { useOnLogout } from '../../../../redux/slices/appInfo/useOnLogout';
import { helpArticleUrl } from '../../../support/helpCenter/helpCenterUrl';
import { accountMessages } from './accountMessages';
import { DeleteAccountModal } from './DeleteAccountModal';
import { Panel } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  email: string;
  /** The home workspace: account deletion removes it. */
  workspaceName: string;
}

/** Sign out and Delete account (design settings-redesign b1, b3). */
const AccountActionsCardsComponent: React.FC<Props> = ({ email, workspaceName }) => {
  const intl = useIntl();
  const onLogout = useOnLogout(useUserToken());
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const openDelete = useCallback(() => setDeleteOpen(true), []);
  const closeDelete = useCallback(() => setDeleteOpen(false), []);

  return (
    <>
      <Panel as="section">
        <div className={cards.row}>
          <span className={cards.rowIcon}>
            <FontAwesomeIcon icon={faArrowRightFromBracket} />
          </span>
          <div className={cards.rowText}>
            <span className={cards.rowTitle}>{intl.formatMessage(accountMessages.signOutTitle)}</span>
            <span className={cards.rowSub}>{intl.formatMessage(accountMessages.signOutDescription)}</span>
          </div>
          <div className={cards.rowEnd}>
            <Button color="secondary" outline size="sm" onClick={onLogout}>
              {intl.formatMessage(accountMessages.signOutTitle)}
            </Button>
          </div>
        </div>
      </Panel>

      <Panel as="section" tone="danger">
        <div className={cards.row}>
          <span className={`${cards.rowIcon} ${cards.rowIconDanger}`}>
            <FontAwesomeIcon icon={faTrashCan} />
          </span>
          <div className={cards.rowText}>
            <span className={cards.rowTitle}>{intl.formatMessage(accountMessages.deleteTitle)}</span>
            <span className={cards.rowSub}>
              {intl.formatMessage(accountMessages.deleteDescription, { workspace: workspaceName })}
            </span>
          </div>
          <div className={cards.rowEnd}>
            <a
              className={cards.link}
              href={helpArticleUrl('account-and-privacy', 'what-deletion-removes-and-keeps')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage(accountMessages.deleteHelp)}
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </a>
            <Button color="danger" outline size="sm" onClick={openDelete}>
              {intl.formatMessage(accountMessages.deleteTitle)}
            </Button>
          </div>
        </div>
      </Panel>

      <DeleteAccountModal isOpen={isDeleteOpen} email={email} workspaceName={workspaceName} onClose={closeDelete} />
    </>
  );
};

export const AccountActionsCards = TypedMemo(AccountActionsCardsComponent);
